/**
 * Fraction Visual Type Resolver
 *
 * Determines the appropriate fraction visual type based on question context.
 * Supported types: 'pizza' (circular) | 'bar' (rectangular)
 *
 * §2.2D ARCHITECTURE NOTE:
 * This resolver is intentionally separate from the general visual governance system.
 * Fractions are the ONLY visual type that needs context-dependent subtype selection
 * (pizza vs bar based on question text). All other visual types are determined
 * statically by the governance map (auto-generated via generateGovernanceMapFromSeeds).
 *
 * Integration points:
 * - Called by FractionVisual.tsx to pick pizza/bar rendering
 * - Called by VisualAidFactory.tsx when type === 'fraction'
 * - NOT part of the governance pipeline (visual type is always 'fraction' in governance)
 *
 * This is a CORRECT special case, not technical debt.
 * See also: src/visuals/components/FractionVisual.tsx
 */

export type FractionVisualType = 'pizza' | 'bar'

interface TypeDetectionResult {
  type: FractionVisualType
  confidence: 'high' | 'medium' | 'low'
  reason: string
}

/**
 * Analyze question text and determine the most appropriate fraction visual type.
 *
 * Priority:
 * 1. Explicit keywords in question text (high confidence)
 * 2. Default to 'pizza' for circular/food context, 'bar' for comparison/strip context
 *
 * @param questionText - The question text to analyze
 * @param options - Optional array of question options to analyze
 * @returns Detection result with type, confidence, and reason
 */
export function detectFractionVisualType(
  questionText: string,
  options?: string[],
): TypeDetectionResult {
  const text = questionText.toLowerCase()
  const allText = options ? `${text} ${options.join(' ').toLowerCase()}` : text

  // High confidence: explicit circular/pizza context
  if (
    allText.includes('pizza') ||
    allText.includes('taart') ||
    allText.includes('cake')
  ) {
    return {
      type: 'pizza',
      confidence: 'high',
      reason: 'Question mentions circular food item',
    }
  }

  // High confidence: bar/strip context
  if (
    allText.includes('reep') ||
    allText.includes('balk') ||
    allText.includes('strook') ||
    allText.includes('chocola')
  ) {
    return {
      type: 'bar',
      confidence: 'high',
      reason: 'Question mentions bar/strip context',
    }
  }

  // Bar is better for comparison questions (vergelijk/groter/kleiner)
  if (
    allText.includes('vergelijk') ||
    allText.includes('groter') ||
    allText.includes('kleiner') ||
    allText.includes('gelijk')
  ) {
    return {
      type: 'bar',
      confidence: 'medium',
      reason: 'Comparison question — bar visual aids side-by-side comparison',
    }
  }

  // Default: pizza for general fraction questions
  return {
    type: 'pizza',
    confidence: 'low',
    reason: 'Default circular visual for fraction questions',
  }
}

/**
 * Check if a question should have a fraction visual at all.
 */
export function shouldShowFractionVisual(questionText: string): boolean {
  const text = questionText.toLowerCase()

  const hasFractionNotation = /\d+\/\d+/.test(text) || /\\frac\{/.test(text)

  const fractionWords = [
    'breuk',
    'een derde',
    'een kwart',
    'een half',
    'twee derde',
    'drie kwart',
    'welke breuk',
    'hoort bij',
  ]
  const hasFractionWords = fractionWords.some((word) => text.includes(word))

  // Exclude pure arithmetic questions
  const isArithmetic =
    text.includes('wat is') &&
    (text.includes('de helft van') ||
      text.includes('hoeveel is') ||
      text.includes('reken uit')) &&
    !hasFractionNotation

  if (isArithmetic) return false

  return hasFractionNotation || hasFractionWords
}

/**
 * Get the appropriate visual type for a question, with fallback logic.
 */
export function resolveFractionVisualType(
  questionText: string,
  options?: string[],
  explicitType?: FractionVisualType,
): FractionVisualType {
  // Explicit type always wins
  if (explicitType === 'pizza' || explicitType === 'bar') {
    return explicitType
  }

  // Detect from context
  const detection = detectFractionVisualType(questionText, options)
  return detection.type
}
